import { useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Switch } from '@finn-auto/design-system/atoms/switch';
import {
  deleteMyData,
  fetchMyData,
  JoinError,
  setMyUpdates,
  type JoinLanguage,
  type PersonView,
} from '../../services/talentJoinApi';
import { JOIN_COPY, initialLanguage } from './joinCopy';
import { JoinShell } from './JoinShell';
import { useJoinConfig } from './useJoinConfig';

const fmtDate = (iso: string | null, language: JoinLanguage) =>
  iso ? new Date(iso).toLocaleDateString(language === 'de' ? 'de-DE' : 'en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';

/**
 * PUBLIC: /join/manage/:token — the person's own page. Shows everything we hold, lets them switch
 * FINN news on or off, and deletes them on request. The token from their confirmation is the only
 * credential; a deleted person's link stops working.
 */
export default function JoinManageView() {
  const { token = '' } = useParams<{ token: string }>();
  const { search } = useLocation();
  const [language, setLanguage] = useState<JoinLanguage>(() => initialLanguage(search, navigator.language));
  const config = useJoinConfig();
  const copy = JOIN_COPY[language];
  const [person, setPerson] = useState<PersonView | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'invalid' | 'error' | 'deleted'>('loading');
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  // A failed news switch is said beside the switch: the page, and the delete button, stay.
  const [toggleFailed, setToggleFailed] = useState(false);
  // Same for a failed delete: the button stays, so the person can simply try again.
  const [deleteFailed, setDeleteFailed] = useState(false);
  const deleting = useRef(false);

  useEffect(() => {
    let alive = true;
    fetchMyData(token)
      .then((p) => {
        if (!alive) return;
        setPerson(p);
        setState('ready');
      })
      .catch((err) => alive && setState(err instanceof JoinError && err.status === 404 ? 'invalid' : 'error'));
    return () => {
      alive = false;
    };
  }, [token]);

  async function toggleUpdates(next: boolean) {
    setBusy(true);
    setToggleFailed(false);
    try {
      setPerson(await setMyUpdates(token, next));
    } catch {
      setToggleFailed(true);
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (deleting.current) return;
    deleting.current = true;
    setBusy(true);
    setDeleteFailed(false);
    try {
      await deleteMyData(token);
      setPerson(null);
      setState('deleted');
    } catch {
      setDeleteFailed(true);
      deleting.current = false;
    } finally {
      setBusy(false);
    }
  }

  const clusterName = (id: string | null) => config.data?.clusters.find((c) => c.id === id)?.[language] || id || '—';

  return (
    <JoinShell language={language} onLanguage={setLanguage} live={config.data?.live}>
      {state === 'loading' && <p className="text-gray-600">{copy.loading}</p>}
      {state === 'invalid' && <p className="text-gray-700">{copy.invalidLink}</p>}
      {state === 'error' && <p role="alert" className="text-red-700">{copy.errors.failed}</p>}
      {state === 'deleted' && (
        <div>
          <h1 className="mb-2 text-2xl font-semibold text-gray-900">{copy.deletedTitle}</h1>
          <p className="text-gray-700">{copy.deletedBody}</p>
        </div>
      )}
      {state === 'ready' && person && (
        <div>
          <h1 className="mb-6 text-2xl font-semibold text-gray-900">{copy.manageTitle}</h1>
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">{copy.whatWeHold}</h2>
          <dl className="mb-6 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
            <dt className="text-gray-500">{copy.name}</dt>
            <dd>{person.firstName} {person.lastName}</dd>
            <dt className="text-gray-500">{copy.email}</dt>
            <dd>{person.email}</dd>
            <dt className="text-gray-500">LinkedIn</dt>
            <dd className="break-all">{person.linkedinUrl || '—'}</dd>
            <dt className="text-gray-500">{copy.cluster}</dt>
            <dd>{clusterName(person.cluster)}</dd>
            <dt className="text-gray-500">{copy.consentUntil}</dt>
            <dd>{fmtDate(person.expiresAt, language)}</dd>
          </dl>

          <div className="mb-8 flex items-center justify-between gap-4 border-t border-gray-200 pt-6">
            <span className="text-sm text-gray-800">{copy.updatesLabel}</span>
            <Switch checked={person.consent.updates} disabled={busy} onCheckedChange={(v) => toggleUpdates(v === true)}
              aria-label={copy.updatesLabel} />
          </div>
          {toggleFailed && <p role="alert" className="-mt-6 mb-8 text-sm text-red-700">{copy.errors.failed}</p>}

          <div className="border-t border-gray-200 pt-6">
            <h2 className="mb-1 font-semibold text-gray-900">{copy.deleteTitle}</h2>
            <p className="mb-4 text-sm text-gray-600">{copy.deleteBody}</p>
            {deleteFailed && <p role="alert" className="mb-3 text-sm text-red-700">{copy.errors.failed}</p>}
            {asking ? (
              <div className="flex gap-2">
                <Button variant="primary" loading={busy} onClick={remove}>
                  {copy.deleteConfirm}
                </Button>
                <Button variant="secondary" onClick={() => setAsking(false)}>
                  {copy.cancel}
                </Button>
              </div>
            ) : (
              <Button variant="secondary" onClick={() => setAsking(true)}>
                {copy.deleteButton}
              </Button>
            )}
          </div>
        </div>
      )}
    </JoinShell>
  );
}
